import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { FiTrash2, FiAlertCircle, FiUser } from "react-icons/fi";
import { FaPlay } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";

const getVideoThumbnail = (videoUrl, second = 1) => {
  return videoUrl
    .replace("/upload/", `/upload/so_${second}/`)
    .replace(".mp4", ".jpg");
};

const SkeletonCard = () => (
  <div className="relative rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse">
    {/* Delete button placeholder */}
    <div className="absolute top-4 right-4 w-8 h-8 bg-gray-500 rounded-full z-20" />

    {/* Sequence badge placeholder */}
    <div className="absolute top-4 left-4 w-8 h-8 bg-gray-500 rounded-full z-20" />

    {/* Thumbnail placeholder */}
    <div className="relative pb-[140%] px-[20%] h-0 bg-gray-200">
      <div className="absolute inset-0 bg-gray-300" />

      {/* Play button placeholder */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-400/40 border border-gray-500/50 rounded-full flex items-center justify-center shadow-md">
          <FaPlay className="text-gray-200 text-lg md:text-xl ml-0.5" />
        </div>
      </div>
    </div>

    {/* Gradient overlay placeholder */}
    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-400 to-transparent" />

    {/* Title placeholder */}
    <div className="absolute bottom-4 left-4 right-4 h-4 bg-gray-500 rounded w-3/4 mx-auto" />
  </div>
);

const VideoCard = ({ video, onPlay, playingVideo, onDelete, index }) => {
  const handleDelete = () => {
    Swal.fire({
      title: "Delete Testimonial?",
      text: `This will permanently delete "${video.title}"`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(video._id);
      }
    });
  };

  return (
    <motion.div
      key={video._id}
      className="relative rounded-2xl shadow-lg overflow-hidden border border-gray-100 group"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      {/* Delete Button */}
      <button
        onClick={handleDelete}
        className="absolute top-4 right-4 bg-red-500 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-red-600 text-white p-2 rounded-full shadow z-20"
      >
        <FiTrash2 size={16} />
      </button>

      {/* Sequence Badge */}
      {video?.sequence !== 0 && (
        <div className="absolute top-4 left-4 text-sm w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white font-bold text-lg shadow-lg z-20">
          {video.sequence}
        </div>
      )}

      {/* Video / Thumbnail */}
      <div className="relative pb-[140%] px-[20%] h-0 overflow-hidden rounded-lg shadow-lg group cursor-pointer">
        {playingVideo === video._id ? (
          <video
            src={video.videoUrl}
            className="absolute top-0 left-0 w-full h-full object-cover"
            controls
            autoPlay
            onPlay={() => onPlay(video)} // ensures others pause
          />
        ) : (
          <>
            <div
              className="absolute top-0 left-0 w-full h-full bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
              style={{
                backgroundImage: `url(${getVideoThumbnail(video.videoUrl)})`,
              }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all duration-300"></div>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onPlay(video);
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-all duration-300">
                <FaPlay className="text-white text-lg md:text-xl ml-0.5" />
              </div>
            </button>
            {video.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <h3 className="text-white font-semibold text-sm md:text-base truncate">
                  {video.title}
                </h3>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

const TestimonialsGrid = ({
  testimonials,
  onDelete,
  playingVideo,
  setPlayingVideo,
}) => {
  const handlePlay = (video) => {
    setPlayingVideo(video._id);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
      {testimonials.map((video, index) => (
        <VideoCard
          key={video._id}
          video={video}
          index={index}
          onDelete={onDelete}
          onPlay={handlePlay}
          playingVideo={playingVideo}
        />
      ))}
    </div>
  );
};

const AdminTestimonials = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null); // 🎯 Only one playing

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      setError("");
      const resp = await axiosInstance.get("/testimonials");
      setTestimonials(resp?.data || []);
      setFilteredItems(resp?.data || []);
    } catch (error) {
      setError(error?.message || "Couldn't load testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleDelete = async (_id) => {
    try {
      await axiosInstance.delete(`/testimonials/${_id}`);
      setFilteredItems((prev) => prev.filter((item) => item._id !== _id));
      if (playingVideo === _id) setPlayingVideo(null); // stop if deleted
    } catch (error) {
      setError(error?.message || "Testimonial couldn't be deleted");
    }
  };

  const handleSearchChange = (query) => setSearchQuery(query);
  const handleAddNewClick = () => navigate("/admin-create-testimonials");

  useEffect(() => {
    setFilteredItems(
      testimonials.filter((item) =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, testimonials]);

  return (
    <div className="flex flex-col w-full h-screen bg-gray-50">
      <AdminSearchBar
        title="Testimonials Management"
        onAddNewClick={handleAddNewClick}
        onSearchClick={handleSearchChange}
      />

      <div className="flex-1 overflow-y-auto p-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-96 text-red-500">
            <FiAlertCircle size={48} />
            <p className="mt-2">{error}</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-96 text-gray-500">
            <FiUser size={48} />
            <p className="mt-4">No testimonials found</p>
          </div>
        ) : (
          <TestimonialsGrid
            testimonials={filteredItems}
            onDelete={handleDelete}
            playingVideo={playingVideo}
            setPlayingVideo={setPlayingVideo}
          />
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
